import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMConversations } from '@ltvco/refresh-lib/hooks';
import { unformatPhone } from '@ltvco/refresh-lib/utils';
import { useServiceToken } from '@ltvco/refresh-lib/v1';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FUNCTION_CALL_OUTPUT,
  LOADING_STATUS,
  TRIGGER_SEARCH,
  TEASER_FOLLOW_UP_MESSAGE,
} from 'components/ChatBV/constants';
import {
  useAncestorSearchOption,
  useChatConversation,
  useImageSearch,
  useObituarySearchOption,
  useReportLoader,
  useTeaserLoader,
  useCourtOpinionLoader,
} from 'components/ChatBV/hooks';
import {
  Message,
  LoadingStatus,
  GeneratedReport,
  MessageReportData,
  GenericReportResponse,
  PossibleReportParams,
  FunctionCallResponse,
  GenericLLMRequest,
  GenericLLMRequestItem,
  CourtCaseData,
} from 'components/ChatBV/types';
import { ROLES } from 'components/SearchpalChatbot/utils/constants';
import { useBaseCourtApi } from 'routes/Dashboard/CourtCases/v2/shared/hooks';

import { useContactSearchOption } from '../hooks/useContactSearchOption';
import {
  buildUserRequest,
  handleFunctionCalls,
  extractAttachedData,
} from '../utils/conversationUtils';
import { isPdfFile } from '../utils/generalUtils';

interface ChatContextType {
  handleUpgradePlanClick: () => void;
  messages: Message[];
  sendMessage: (
    content: string,
    imageFile?: File,
    useWebSearch?: boolean
  ) => void;
  setMessages: (messages: Message[]) => void;
  sendPersonFollowUpMessage: (name: string, reports: MessageReportData) => void;
  sendCourtCaseMessage: (
    courtCaseName: string,
    courtCaseData: CourtCaseData
  ) => void;
  addMessageDirectly: (
    userContent: string,
    assistantContent: string,
    courtOpinionData?: any,
    opinionSummaryContextId?: string
  ) => Promise<void>;
  handleNewChat: () => void;
  generatedReports: Record<string, GeneratedReport>;
  handleReportsSelected?: (selectedReport: PossibleReportParams) => void;
  loadingStatus: LoadingStatus;
  imageUploadError: string | null;
  setImageUploadError: (error: string | null) => void;
  useWebSearch: boolean;
  setUseWebSearch: (value: boolean) => void;
  setContextId: (contextId: string | undefined) => void;
  handleConversation: (request: GenericLLMRequest) => Promise<any>;
  setSidebarCollapsed: (collapsed: boolean) => void;
  sidebarCollapsed: boolean;
  collapseAllSectionsTrigger: number;
  lastContextId: string | undefined;
}

export const ChatContext = createContext<ChatContextType>({
  handleUpgradePlanClick: () => {},
  messages: [],
  setMessages: () => {},
  sendMessage: () => {},
  sendPersonFollowUpMessage: () => {},
  sendCourtCaseMessage: () => {},
  addMessageDirectly: async () => {},
  handleNewChat: () => {},
  generatedReports: {},
  handleReportsSelected: () => {},
  loadingStatus: {
    message: '',
    subtitle: '',
  },
  imageUploadError: null,
  setImageUploadError: () => {},
  useWebSearch: false,
  setUseWebSearch: () => {},
  setContextId: () => {},
  handleConversation: async () => ({}),
  lastContextId: undefined,
  setSidebarCollapsed: () => {},
  sidebarCollapsed: false,
  collapseAllSectionsTrigger: 0,
});

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const {
    handleConversation,
    sendMessageToLLM,
    userOrientedMessages,
    setUserOrientedMessages,
    lastContextId,
    setLastContextId,
    loadingStatus,
    setLoadingStatus,
    generatedReports,
    setGeneratedReports,
  } = useChatConversation();

  const { logError } = useContext(AppConfig);
  const { data: tokenData } = useServiceToken('insight');
  const { searchCaseLaw, searchPACER } = useBaseCourtApi();
  const searchAncestor = useAncestorSearchOption();
  const searchObituary = useObituarySearchOption();
  const searchContact = useContactSearchOption();
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [useWebSearch, setUseWebSearch] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [collapseAllSectionsTrigger, setCollapseAllSectionsTrigger] =
    useState(0);
  const { refetchConversations } = useLLMConversations();

  const triggerCollapseAllSections = () => {
    setCollapseAllSectionsTrigger((prev) => prev + 1);
  };
  const previousContextIdRef = useRef<string | undefined>(undefined);

  // Refresh conversation list when a new conversation is created
  useEffect(() => {
    // Detect when lastContextId changes from undefined to a value (new conversation created)
    if (
      previousContextIdRef.current === undefined &&
      lastContextId !== undefined
    ) {
      // A new conversation was created, refresh the list
      refetchConversations().catch((error) => {
        logError('Error refreshing conversation list', error as Error);
      });
    }
    // Update the ref to track the current value
    previousContextIdRef.current = lastContextId;
  }, [lastContextId, refetchConversations, logError]);

  // Load reports for messages that have permalink and searchType but no reportData
  useReportLoader({
    messages: userOrientedMessages,
    setMessages: setUserOrientedMessages,
    generatedReports,
    setGeneratedReports,
  });

  const searchOptions = useMemo(
    () => ({
      searchObituary,
      searchAncestor,
      searchContact,
      searchCourtCases: (query: string) =>
        searchPACER(query, 'score desc', {}).then((r) => ({
          results: r.results ?? [],
          count: r.count ?? r.results?.length ?? 0,
        })),
      searchCourtOpinions: (query: string) =>
        searchCaseLaw(query, 'score desc', {}).then((r) => ({
          results: r.results,
          count: r.count ?? r.results?.length ?? 0,
        })),
    }),
    [searchObituary, searchAncestor, searchContact, searchPACER, searchCaseLaw]
  );

  // Load teasers for person and other search types (obituary, ancestor, contact, court cases, court opinions) from history
  useTeaserLoader({
    messages: userOrientedMessages,
    setMessages: setUserOrientedMessages,
    searchOptions,
  });

  // Load court opinion data for messages that have cluster_id but no courtOpinionData
  useCourtOpinionLoader({
    messages: userOrientedMessages,
    setMessages: setUserOrientedMessages,
  });

  const extractSearchValueFromReport = (
    report: PossibleReportParams
  ): string | null => {
    const searchType = report.search_type;
    if (searchType === 'phone' && report.phone_number) {
      let phoneNumber = unformatPhone(report.phone_number);
      if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
        phoneNumber = phoneNumber.substring(1);
      }
      return phoneNumber;
    }
    if (searchType === 'email' && report.email) {
      return report.email;
    }
    if (searchType === 'vehicle') {
      if (report.vehicle_vin) {
        return report.vehicle_vin;
      }
      if (report.vehicle_license_plate && report.state) {
        return `${report.vehicle_license_plate} ${report.state}`;
      }
      if (report.vehicle_year && report.vehicle_make && report.vehicle_model) {
        let value = `${report.vehicle_year} ${report.vehicle_make} ${report.vehicle_model}`;
        if (report.vehicle_trim) {
          value += ` ${report.vehicle_trim}`;
        }
        return value;
      }
    }
    if (searchType === 'property') {
      if (report.address && report.city && report.state) {
        let value = `${report.address}, ${report.city}, ${report.state}`;
        if (report.zipcode) {
          value += ` ${report.zipcode}`;
        }
        return value;
      }
    }
    return null;
  };

  const handleReportsSelected = async (
    selectedReport: PossibleReportParams,
    skipIdleCheck = false
  ) => {
    if (!skipIdleCheck && loadingStatus !== LOADING_STATUS.IDLE) {
      return;
    }

    if (loadingStatus !== LOADING_STATUS.THINKING) {
      setLoadingStatus(LOADING_STATUS.THINKING);
    }

    const reportTypeName =
      selectedReport.search_type === 'person'
        ? 'Person'
        : selectedReport.search_type === 'phone'
        ? 'Phone'
        : selectedReport.search_type === 'email'
        ? 'Email'
        : selectedReport.search_type === 'property'
        ? 'Property'
        : selectedReport.search_type === 'vehicle'
        ? 'Vehicle'
        : 'Report';

    const userMessage: Message = {
      content: `Run ${reportTypeName} Report`,
      role: ROLES.user,
    };
    setUserOrientedMessages((prev) => [...prev, userMessage]);
    setSidebarCollapsed(true);
    triggerCollapseAllSections();

    try {
      const searchValue = extractSearchValueFromReport(selectedReport);
      const isDirectSearch =
        selectedReport.search_type === 'phone' ||
        selectedReport.search_type === 'email' ||
        selectedReport.search_type === 'vehicle' ||
        selectedReport.search_type === 'property';

      let textContextId: string | undefined;
      let textCallId: string | undefined;
      let triggerSearchCall: FunctionCallResponse | undefined;

      if (isDirectSearch && searchValue) {
        const textResponse = await sendMessageToLLM(
          buildUserRequest(
            { content: searchValue, role: ROLES.user },
            undefined
          )
        );

        const [, functionCallsFromText] = textResponse.data as [
          string,
          FunctionCallResponse[] | null
        ];

        if (functionCallsFromText && Array.isArray(functionCallsFromText)) {
          triggerSearchCall = functionCallsFromText.find(
            (fc: FunctionCallResponse) => fc.name === TRIGGER_SEARCH
          );

          if (triggerSearchCall) {
            textContextId = textResponse.context_id;
            textCallId = triggerSearchCall.call_id;
          }
        }
      }

      const functionCallToProcess: FunctionCallResponse =
        triggerSearchCall ||
        ({
          name: TRIGGER_SEARCH,
          call_id: `selected_${Date.now()}_${Math.random()}`,
          arguments: selectedReport as any,
          type: 'function_call',
          status: 'completed',
        } as FunctionCallResponse);

      const processedFunctionCalls = await handleFunctionCalls(
        [functionCallToProcess],
        generatedReports,
        {
          searchCourtCases: (query: string) =>
            searchPACER(query, 'score desc', {}).then((r) => ({
              results: r.results ?? [],
              count: r.count ?? r.results?.length ?? 0,
            })),
          searchCourtOpinions: (query: string) =>
            searchCaseLaw(query, 'score desc', {}).then((r) => ({
              results: r.results,
              count: r.count ?? r.results?.length ?? 0,
            })),
          searchAncestor,
          searchObituary,
          searchContact,
        }
      );
      const searchResult = processedFunctionCalls[0];

      if (
        searchResult?.output &&
        typeof searchResult.output === 'string' &&
        searchResult.output.includes('error')
      ) {
        throw new Error(searchResult.output);
      }

      if (searchResult?.teaser) {
        const assistantMessage: Message = {
          context_id: textContextId || lastContextId || '',
          content: `We found multiple **${reportTypeName}** results. Please select one to view the full report.`,
          role: ROLES.assistant,
          response_id: `response_${Date.now()}`,
          teaser: searchResult.teaser,
          params: searchResult.params,
        };

        setTimeout(() => {
          setUserOrientedMessages((prev) => [...prev, assistantMessage]);
        }, 1500);
      } else {
        if (searchResult?.permalink && searchResult?.reportData) {
          const functionCallOutput: GenericLLMRequestItem = {
            type: FUNCTION_CALL_OUTPUT,
            call_id: textCallId || searchResult.call_id,
            output: searchResult.output,
            role: null,
            content: null,
            metadata: JSON.stringify({
              permalink: searchResult.permalink,
              searchType: searchResult.searchType,
            }),
          };

          const summaryRequest: GenericLLMRequest = {
            items: [functionCallOutput],
            context_id: textContextId || lastContextId || null,
          };

          const summaryResponse = await handleConversation(summaryRequest);

          const assistantMessage: Message = {
            context_id:
              summaryResponse.context_id ||
              textContextId ||
              lastContextId ||
              '',
            content: summaryResponse.message,
            role: ROLES.assistant,
            response_id: summaryResponse.response_id,
            reports: [
              {
                reportData: searchResult.reportData,
                type: searchResult.searchType!,
                permalink: searchResult.permalink,
              },
            ],
            sources: summaryResponse.sources,
            executedFunctionCall: summaryResponse.executedFunctionCall,
          };

          setTimeout(() => {
            setUserOrientedMessages((prev) => [...prev, assistantMessage]);
          }, 1500);
        } else {
          throw new Error(
            `We couldn't find any results for the ${reportTypeName.toLowerCase()} information provided. Please check the details and try again.`
          );
        }
      }
    } catch (_error) {
      const reportTypeName =
        selectedReport.search_type === 'person'
          ? 'Person'
          : selectedReport.search_type === 'phone'
          ? 'Phone'
          : selectedReport.search_type === 'email'
          ? 'Email'
          : selectedReport.search_type === 'property'
          ? 'Property'
          : selectedReport.search_type === 'vehicle'
          ? 'Vehicle'
          : 'Report';

      const errorMessage = `We couldn't find any results for the ${reportTypeName.toLowerCase()} information provided. Please check the details and try again.`;

      const assistantMessage: Message = {
        context_id: lastContextId || '',
        content: errorMessage,
        role: ROLES.assistant,
        response_id: `response_${Date.now()}`,
      };

      setTimeout(() => {
        setUserOrientedMessages((prev) => [...prev, assistantMessage]);
      }, 1500);
    } finally {
      setTimeout(() => {
        setLoadingStatus(LOADING_STATUS.IDLE);
      }, 1000);
    }
  };

  const { processImageSearch } = useImageSearch({
    logError,
    token: tokenData?.token,
    lastContextId,
    generatedReports,
    setUserOrientedMessages,
    setLoadingStatus,
    handleConversation,
    sendMessageToLLM,
    handleReportsSelected,
  });

  const sendMessage = async (
    content: string,
    imageFile?: File,
    useWebSearchParam?: boolean
  ) => {
    // Use the state flag or parameter (parameter takes precedence for backward compatibility)
    // Don't use web search for image uploads
    const shouldUseWebSearch = imageFile
      ? false
      : useWebSearchParam ?? useWebSearch;

    const { cleanContent, attachedData } = extractAttachedData(content);

    const userMessage: Message = {
      content: cleanContent,
      role: ROLES.user,
      imageUrl: imageFile ? URL.createObjectURL(imageFile) : undefined,
      isPdf: isPdfFile(imageFile),
    };

    // Add user message to the list immediately
    setUserOrientedMessages((prev) => [...prev, userMessage]);
    setSidebarCollapsed(true);
    triggerCollapseAllSections();

    // If attached-data is present, create assistant message with report or loading state immediately
    if (attachedData?.permalink && attachedData?.reportType) {
      const existingReport = generatedReports[attachedData.permalink];

      const attachedDataAssistantMessage: Message = existingReport
        ? {
            content: '',
            role: ROLES.assistant,
            reports: [
              {
                reportData: existingReport.reportData,
                type: existingReport.searchType,
                permalink: existingReport.permalink,
              },
            ],
            executedFunctionCall: TRIGGER_SEARCH,
          }
        : {
            content: '',
            role: ROLES.assistant,
            permalink: attachedData.permalink,
            searchType: attachedData.reportType,
            isLoadingReport: true,
            executedFunctionCall: TRIGGER_SEARCH,
          };

      setTimeout(() => {
        setUserOrientedMessages((prev) => [
          ...prev,
          attachedDataAssistantMessage,
        ]);
      }, 1500);
    }

    setLoadingStatus(LOADING_STATUS.THINKING);
    setUseWebSearch(false);

    try {
      if (imageFile) {
        try {
          const { contextId: imageContextId, handled } =
            await processImageSearch(imageFile, lastContextId);

          if (!handled && imageContextId) {
            const response = await handleConversation(
              buildUserRequest(userMessage, imageContextId ?? undefined, false)
            );

            // Process attached-data from response
            const { cleanContent: responseCleanContent } = extractAttachedData(
              response.message || ''
            );

            const assistantMessage: Message = {
              context_id: response.context_id,
              content: responseCleanContent,
              role: ROLES.assistant,
              response_id: response.response_id,
              reports: response.reports,
              teaser: response.teaser,
              params: response.params,
              possibleReports: response.possibleReports,
              sources: response.sources,
              executedFunctionCall: response.executedFunctionCall,
              teaserSearchType: response.teaserSearchType,
            };

            setTimeout(() => {
              setUserOrientedMessages((prev) => [...prev, assistantMessage]);
            }, 1500);
            setTimeout(() => {
              setLoadingStatus(LOADING_STATUS.IDLE);
            }, 1000);
          }
        } catch (_error) {
          const errorMessage: Message = {
            content: "We couldn't process the file. Please try again.",
            role: ROLES.assistant,
          };
          setTimeout(() => {
            setUserOrientedMessages((prev) => [...prev, errorMessage]);
          }, 1500);
          setTimeout(() => {
            setLoadingStatus(LOADING_STATUS.IDLE);
          }, 1000);
        }
      } else {
        const response = await handleConversation(
          buildUserRequest(userMessage, lastContextId, shouldUseWebSearch)
        );

        // Process attached-data from response
        const { cleanContent: responseCleanContent } = extractAttachedData(
          response.message || ''
        );

        const assistantMessage: Message = {
          context_id: response.context_id,
          content: responseCleanContent,
          role: ROLES.assistant,
          response_id: response.response_id,
          reports: response.reports,
          teaser: response.teaser,
          params: response.params,
          possibleReports: response.possibleReports,
          sources: response.sources,
          executedFunctionCall: response.executedFunctionCall,
          teaserSearchType: response.teaserSearchType,
        };

        setTimeout(() => {
          setUserOrientedMessages((prev) => [...prev, assistantMessage]);
        }, 1500);
        setTimeout(() => {
          setLoadingStatus(LOADING_STATUS.IDLE);
        }, 1000);
      }
    } catch (_error) {
      setLoadingStatus(LOADING_STATUS.IDLE);
    }
  };

  const sendPersonFollowUpMessage = async (
    name: string,
    reports: MessageReportData
  ) => {
    if (loadingStatus !== LOADING_STATUS.IDLE) {
      return;
    }
    const userMessage: Message = {
      content: TEASER_FOLLOW_UP_MESSAGE[reports.type]?.(name) || '',
      role: ROLES.user,
    };

    setUserOrientedMessages((prev) => [...prev, userMessage]);
    setSidebarCollapsed(true);
    triggerCollapseAllSections();

    setLoadingStatus(LOADING_STATUS.THINKING);

    // Store the report in generatedReports first
    setGeneratedReports((prev) => ({
      ...prev,
      [reports.permalink]: {
        reportData: reports.reportData as GenericReportResponse,
        searchType: reports.type as string,
        permalink: reports.permalink,
      },
    }));

    // Send only permalink and report type to smartsearchbot
    const messageContent = `Tell me about ${name}[attached-data: {permalink: ${reports.permalink}, report-Type: ${reports.type}}]`;
    const summaryRequest = buildUserRequest(
      { content: messageContent, role: ROLES.user },
      lastContextId
    );

    try {
      const summaryResponse = await handleConversation(summaryRequest);

      // Process attached-data from response
      const { cleanContent: responseCleanContent } = extractAttachedData(
        summaryResponse.message || ''
      );

      const assistantMessage: Message = {
        context_id: summaryResponse.context_id || lastContextId || '',
        content:
          responseCleanContent ||
          `Here are the results for the ${name} search:`,
        role: ROLES.assistant,
        response_id: summaryResponse.response_id,
        reports: [
          {
            reportData: reports.reportData,
            type: reports.type,
            permalink: reports.permalink,
          },
        ],
        executedFunctionCall: TRIGGER_SEARCH,
      };

      setTimeout(() => {
        setUserOrientedMessages((prev) => [...prev, assistantMessage]);
      }, 1500);
    } catch (_error) {
      // Fallback to original behavior if sending to smartsearchbot fails
      const assistantMessage: Message = {
        content: `Here are the results for the ${name} search:`,
        role: ROLES.assistant,
        reports: [
          {
            reportData: reports.reportData,
            type: reports.type,
            permalink: reports.permalink,
          },
        ],
        executedFunctionCall: TRIGGER_SEARCH,
      };
      setTimeout(() => {
        setUserOrientedMessages((prev) => [...prev, assistantMessage]);
      }, 1500);
    }

    setTimeout(() => {
      setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
  };

  const sendCourtCaseMessage = async (
    courtCaseName: string,
    courtCaseData: CourtCaseData
  ) => {
    if (loadingStatus !== LOADING_STATUS.IDLE) {
      return;
    }

    const userMessage: Message = {
      content: `${courtCaseName}`,
      role: ROLES.user,
    };
    setUserOrientedMessages((prev) => [...prev, userMessage]);
    setSidebarCollapsed(true);
    triggerCollapseAllSections();

    setLoadingStatus(LOADING_STATUS.THINKING);

    const assistantMessage: Message = {
      content: `Court Case: ${courtCaseData?.id}`,
      courtCaseData,
      role: ROLES.assistant,
    };

    setTimeout(() => {
      setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 1500);

    setTimeout(() => {
      setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
  };

  const addMessageDirectly = async (
    userContent: string,
    assistantContent: string,
    courtOpinionData?: any,
    opinionSummaryContextId?: string
  ) => {
    // Check if this is a court opinion message
    const isCourtOpinionMessage =
      assistantContent === 'COURT_OPINION_PAGE' || !!courtOpinionData;

    // Only add user message if it has content (not empty)
    if (userContent) {
      const userMessage: Message = {
        content: userContent,
        role: ROLES.user,
      };

      // Add user message to local state immediately
      setUserOrientedMessages((prev) => {
        return [...prev, userMessage];
      });
      setSidebarCollapsed(true);
      triggerCollapseAllSections();

      // For court opinion messages, don't send user message to backend
      // This prevents function calls that could trigger teasers
      // The system message will be sent from CourtOpinionsPage instead
      if (!isCourtOpinionMessage) {
        // Send user message to backend so it appears in conversation history
        try {
          const userRequest = buildUserRequest(
            userMessage,
            lastContextId,
            false
          );
          const response = await handleConversation(userRequest);

          // Update context_id if we got a new one
          if (response.context_id) {
            setLastContextId(response.context_id);
          }
        } catch (error) {
          // If sending fails, continue anyway - the message is already in local state
          logError(
            'Failed to save user message to conversation',
            error as Error
          );
        }
      }
    }

    // Don't send system message here - it will be sent from CourtOpinionsPage
    // when the summary is available. Just create the assistant message for now.
    const assistantMessage: Message = {
      content: assistantContent,
      role: ROLES.assistant,
      context_id: lastContextId || undefined,
      courtOpinionData,
      courtOpinionClusterId: courtOpinionData?.cluster_id,
      opinionSummaryContextId,
      // Explicitly set teaser-related properties to false/undefined for court opinion messages
      isLoadingTeaser: isCourtOpinionMessage ? false : undefined,
      teaser: isCourtOpinionMessage ? undefined : undefined,
    };

    setTimeout(() => {
      setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 500);
  };

  const handleNewChat = () => {
    setUserOrientedMessages([]);
    setLastContextId(undefined);
  };

  const handleUpgradePlanClick = () => {
    navigate('/chatbv/upgrade/plan');
  };

  return (
    <ChatContext.Provider
      value={{
        handleUpgradePlanClick,
        messages: userOrientedMessages,
        setMessages: setUserOrientedMessages,
        sendMessage,
        sendPersonFollowUpMessage,
        sendCourtCaseMessage,
        addMessageDirectly,
        handleNewChat,
        generatedReports,
        handleReportsSelected,
        loadingStatus,
        imageUploadError,
        setImageUploadError,
        useWebSearch,
        setUseWebSearch,
        setContextId: setLastContextId,
        handleConversation,
        lastContextId,
        setSidebarCollapsed,
        sidebarCollapsed,
        collapseAllSectionsTrigger,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
